import { FC, useEffect } from 'react';
import { useHistory } from 'react-router';
import { buildClientUrl } from 'utils/helpers/client';
import { useActiveClientContext } from 'store/active-client';
import Col from 'react-bootstrap/Col';
import FormGroup from 'components/form/FormGroup';
import FormRowGroup from 'components/form/FormRowGroup';
import Form from 'components/form/Form';
import { ISelectOptionProps } from 'components/form/types';
import { IInputs, IDropdowns } from '../types';
import { employerScenarios, employeeScenarios } from '../scenarios';

interface IProps {
  methods: any;
  handleSubmit: (data: IInputs) => void;
  btnLabel: string;
  categories: ISelectOptionProps[];
  templateDataOptions: IDropdowns;
  canEdit?: boolean;
  isMultilingual?: boolean | null;
}

const TemplateForm: FC<IProps> = ({
  methods,
  handleSubmit,
  btnLabel,
  categories,
  templateDataOptions,
  canEdit,
  isMultilingual,
}: IProps) => {
  const history = useHistory();
  const { client } = useActiveClientContext();
  const buildScenario = (scenario: any) => {
    return (
      <ol className="bg-primary d-block pb-3 pr-3 pt-3 rounded text-white">
        {scenario.map((step: string) => (
          <li>{step}</li>
        ))}
      </ol>
    );
  };

  const { watch, unregister } = methods;

  const canBeCreatedBy = watch('can_be_created_by') as ISelectOptionProps;

  const employerScenario = watch('employer_scenario') as ISelectOptionProps;

  const employeeScenario = watch('employee_scenario') as ISelectOptionProps;

  useEffect(() => {
    if (canBeCreatedBy?.value !== 'employer_and_employee') unregister('employee_scenario');
  }, [canBeCreatedBy]);

  return (
    <Form
      methods={methods}
      onSubmit={handleSubmit}
      btnLabel={btnLabel}
      onCancel={() => history.push(buildClientUrl('/document-templates', client))}
      isHorizontal
      isSubmitDisabled={!canEdit}
    >
      {isMultilingual ? (
        <FormRowGroup>
          <FormGroup
            controlId="formGroupName"
            label="Име на шаблона за документ"
            type="text"
            name="name"
            isRequired
            isDisabled={!canEdit}
            isRowGroup
          />
          <FormGroup
            controlId="formGroupNameEn"
            label="Document template name"
            type="text"
            name="name_en"
            isDisabled={!canEdit}
            isRowGroup
          />
        </FormRowGroup>
      ) : (
        <FormGroup
          controlId="formGroupName"
          label="Име на шаблона за документ"
          type="text"
          name="name"
          isRequired
          isHorizontal
          isDisabled={!canEdit}
        />
      )}
      <FormGroup
        controlId="formGroupStoragePeriod"
        label="Срок за съхранение"
        type="select"
        as="select"
        name="storage_period"
        isRequired
        isHorizontal
        selectOptions={templateDataOptions.storage_period}
        isDisabled={!canEdit}
      />
      <FormGroup
        controlId="formGroupCategory"
        label="Категория"
        type="select"
        as="select"
        name="category_id"
        isRequired
        isHorizontal
        selectOptions={categories}
        isDisabled={!canEdit}
      />
      <FormGroup
        controlId="formGroupLeaveIdentifier"
        label="Идентификатор"
        type="select"
        as="select"
        name="identifier"
        isHorizontal
        selectOptions={templateDataOptions.identifiers}
        isClearable
        isDisabled={!canEdit}
      />
      {/** TODO - change options to be loaded from BE when feature is ready */}
      <FormGroup
        controlId="formGroupCanBeCreatedBy"
        label="Може да се създава от"
        type="select"
        as="select"
        name="can_be_created_by"
        isRequired
        isHorizontal
        selectOptions={templateDataOptions.can_be_created_by}
        isDisabled={!canEdit}
      />
      {/** TODO - change options to be loaded from BE when feature is ready */}
      <FormGroup
        controlId="formGroupEmployerScenario"
        label="При създаване от работодател"
        type="select"
        as="select"
        name="employer_scenario"
        isRequired
        isHorizontal
        selectOptions={templateDataOptions.employer_scenario}
        isDisabled={!canEdit}
      >
        {employerScenario?.value && (
          <Col sm={{ offset: 2, span: 6 }}>{buildScenario(employerScenarios[employerScenario.value as number])}</Col>
        )}
      </FormGroup>
      {canBeCreatedBy?.value === 'employer_and_employee' && (
        /** TODO - change options to be loaded from BE when feature is ready */
        <FormGroup
          controlId="formGroupEmployeeScenario"
          label="При създаване от служител"
          type="select"
          as="select"
          name="employee_scenario"
          isRequired
          isHorizontal
          selectOptions={templateDataOptions.employee_scenario}
          isDisabled={!canEdit}
        >
          {employeeScenario?.value && (
            <Col sm={{ offset: 2, span: 6 }}>{buildScenario(employeeScenarios[employeeScenario.value as number])}</Col>
          )}
        </FormGroup>
      )}
      <FormGroup
        controlId="formGroupContents"
        label="Съдържание на шаблона"
        type="wysiwyg-editor"
        name="contents"
        isRequired
        isHorizontal
        isDisabled={!canEdit}
        disableLocalization
      />
      {isMultilingual && (
        <FormGroup
          controlId="formGroupContentsEn"
          label="Template content"
          type="wysiwyg-editor"
          name="contents_en"
          isHorizontal
          isDisabled={!canEdit}
          disableLocalization
        />
      )}
      {isMultilingual ? (
        <FormRowGroup>
          <FormGroup
            controlId="formGroupDescription"
            label="Описание на шаблона"
            as="textarea"
            name="description"
            isDisabled={!canEdit}
            isRowGroup
          />
          <FormGroup
            controlId="formGroupDescriptionEn"
            label="Template description"
            as="textarea"
            name="description_en"
            isDisabled={!canEdit}
            isRowGroup
          />
        </FormRowGroup>
      ) : (
        <FormGroup
          controlId="formGroupDescription"
          label="Описание на шаблона"
          as="textarea"
          name="description"
          isHorizontal
          isDisabled={!canEdit}
        />
      )}
      <FormGroup
        controlId="formGroupStatus"
        label="Активно"
        checkType="switch"
        name="is_active"
        isRequired
        isHorizontal
        isDisabled={!canEdit}
      />
    </Form>
  );
};

export default TemplateForm;
